import React from "react";
import { Star, Quote } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { useApp } from "@/context/AppContext";
import { imgSrc } from "@/lib/api";

export const Stars = ({ value = 5, size = "h-4 w-4", testId }) => (
  <div className="flex items-center gap-0.5" aria-label={`${value} out of 5 stars`} data-testid={testId}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${size} ${i <= value ? "fill-[var(--accent-gold)] text-[var(--accent-gold)]" : "text-[var(--border-subtle)]"}`} />
    ))}
  </div>
);

const Card = ({ t }) => (
  <div className="arch-card flex h-full flex-col p-7" data-testid={`testimonial-${t.id}`}>
    <Quote className="h-6 w-6 text-[var(--accent-gold)] opacity-70" />
    <Stars value={Number(t.rating) || 5} testId={`testimonial-stars-${t.id}`} />
    <p className="mt-4 flex-1 font-serif text-lg font-light leading-relaxed text-[var(--text-primary)]">“{t.message}”</p>
    <div className="mt-6 flex items-center gap-3 border-t border-[var(--border-subtle)] pt-5">
      {t.photo ? (
        <img src={imgSrc(t.photo)} alt={t.name} className="h-12 w-12 rounded-full object-cover ring-2 ring-[var(--accent-a15)]" />
      ) : (
        <div className="flex h-12 w-12 items-center justify-center rounded-full font-serif text-base text-white" style={{ background: "linear-gradient(135deg,#9ED3B8,#0F4A36)" }}>{t.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}</div>
      )}
      <div>
        <div className="text-sm font-semibold text-[var(--text-primary)]">{t.name}</div>
        {t.role && <div className="text-xs text-[var(--text-muted)]">{t.role}</div>}
      </div>
    </div>
  </div>
);

export const Testimonials = () => {
  const { data } = useApp();
  const list = (data.testimonials || []).filter((t) => t.visible !== false);
  if (!list.length) return null;
  const avg = (list.reduce((s, t) => s + (Number(t.rating) || 0), 0) / list.length).toFixed(1);
  return (
    <section className="border-y border-[var(--border-subtle)] bg-[var(--bg-card)] py-20 lg:py-28" data-testid="testimonials-section">
      <div className="container-arch">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="overline">Client Voices</div>
              <h2 className="mt-3 max-w-2xl font-serif text-3xl font-light text-[var(--text-primary)] sm:text-4xl">Perspective, in our clients' words.</h2>
            </div>
            <div className="flex items-center gap-3 rounded-full border border-[var(--border-subtle)] px-5 py-2.5" data-testid="testimonials-average">
              <span className="font-serif text-2xl text-[var(--text-primary)]">{avg}</span>
              <Stars value={Math.round(Number(avg))} />
              <span className="text-xs text-[var(--text-muted)]">{list.length} client{list.length > 1 ? "s" : ""}</span>
            </div>
          </div>
        </Reveal>
        <Stagger className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {list.map((t) => <StaggerItem key={t.id}><Card t={t} /></StaggerItem>)}
        </Stagger>
      </div>
    </section>
  );
};

export default Testimonials;
